import { resources } from "@constants/hardcoded-strings";
import { TabTitleWithErrorCount } from "@containers/common/tab-title-with-error-count/tab-title-with-error-count";
import {
    IdentityProviderCompanyIDPValidationErrors,
    IdentityProviderCustomURLParametersErrors,
    IdentityProviderGeneralInfoValidationErrors,
    IdentityProviderFederatedAttributesErrors,
    IdentityProviderValidationErrors
} from "@idw-types/common/validation/identity-provider/identity-provider-validation-errors";
import { FormattedIdentityProviderDetails } from "@idw-types/data/identity-providers";
import { getErrorAlert } from "@utils/tab-util";

export const getIdentityProviderValidationErrors = (identityProviderDetails: FormattedIdentityProviderDetails): IdentityProviderValidationErrors => {
    const generalInfoErrors = getIdentityProviderGeneralInfoValidationErrors(identityProviderDetails);
    const companyIDPErrors = getIdentityProviderCompanyIDPValidationErrors(identityProviderDetails);
    const customURLParametersErrors = getIdentityProviderCustomURLParametersErrors(identityProviderDetails);
    const federatedAttributesErrors = getIdentityProviderFederatedAttributesErrors(identityProviderDetails);

    const count = generalInfoErrors.count + companyIDPErrors.count + customURLParametersErrors.count + federatedAttributesErrors.count;

    return {
        count,
        generalInfo: generalInfoErrors,
        companyIDP: companyIDPErrors,
        customURLParameters: customURLParametersErrors,
        federatedAttributes: federatedAttributesErrors
    };
};

const getIdentityProviderGeneralInfoValidationErrors = (identityProviderDetails: FormattedIdentityProviderDetails): IdentityProviderGeneralInfoValidationErrors => {
    let nameError: React.ReactNode = null;
    let codeError: React.ReactNode = null;
    let categoryError: React.ReactNode = null;
    let organizationError: React.ReactNode = null;

    let count = 0;

    if (!identityProviderDetails.name) {
        count++;
        nameError = getErrorAlert(resources.common.requiredField);
    } else {
        const pattern = /[^a-zA-Z0-9_+-\s]/g;
        const isInValid = pattern.test(identityProviderDetails.name);
        if (isInValid) {
            count++;
            nameError = getErrorAlert("Unsupported character used. Allowed characters are alphanumeric, hyphens (-), plus signs (+), and underscores (_).");
        }
    }

    if (!identityProviderDetails.code) {
        count++;
        codeError = getErrorAlert(resources.common.requiredField);
    }

    if (!identityProviderDetails.category) {
        count++;
        categoryError = getErrorAlert(resources.common.requiredField);
    }

    if (!identityProviderDetails.company) {
        count++;
        organizationError = getErrorAlert(resources.common.requiredField);
    }

    return {
        count,
        nameError,
        codeError,
        categoryError,
        organizationError,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.identityProviderDetails.tabs.generalInfo.tabName} errorCount={count} />
    };
};

const getIdentityProviderCompanyIDPValidationErrors = (identityProviderDetails: FormattedIdentityProviderDetails): IdentityProviderCompanyIDPValidationErrors => {
    let scopeError: React.ReactNode = null;
    let clientIdError: React.ReactNode = null;
    let clientSecretError: React.ReactNode = null;
    let authorizationURLError: React.ReactNode = null;
    let tokenURLError: React.ReactNode = null;
    let userInfoURLError: React.ReactNode = null;

    let count = 0;

    if (!identityProviderDetails.companyIDPInfo.scope) {
        count++;
        scopeError = getErrorAlert(resources.common.requiredField);
    }

    if (!identityProviderDetails.companyIDPInfo.clientID) {
        count++;
        clientIdError = getErrorAlert(resources.common.requiredField);
    }

    if (!identityProviderDetails.companyIDPInfo.clientSecret) {
        count++;
        clientSecretError = getErrorAlert(resources.common.requiredField);
    }

    if (!identityProviderDetails.companyIDPInfo.authURL) {
        count++;
        authorizationURLError = getErrorAlert(resources.common.requiredField);
    }

    if (!identityProviderDetails.companyIDPInfo.tokenURL) {
        count++;
        tokenURLError = getErrorAlert(resources.common.requiredField);
    }

    if (!identityProviderDetails.companyIDPInfo.userInfoURL) {
        count++;
        userInfoURLError = getErrorAlert(resources.common.requiredField);
    }

    return {
        count,
        scopeError,
        clientIdError,
        clientSecretError,
        authorizationURLError,
        tokenURLError,
        userInfoURLError,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.identityProviderDetails.tabs.organization.tabName} errorCount={count} />
    };
};

const getIdentityProviderCustomURLParametersErrors = (identityProviderDetails: FormattedIdentityProviderDetails): IdentityProviderCustomURLParametersErrors => {
    const customURLParametersErrors: { [key: string]: React.ReactNode } = {};
    let count = 0;

    identityProviderDetails.companyIDPInfo.companyIDPInfoParams.forEach(param => {
        if (!param.key || !param.value || !param.urlType) {
            count++;
            customURLParametersErrors[param.uuid] = getErrorAlert(resources.common.allRequiredFields);
        }
    });

    return {
        count,
        customURLParametersErrors,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.identityProviderDetails.tabs.urlParameters.tabName} errorCount={count} />
    }
};

const getIdentityProviderFederatedAttributesErrors = (identityProviderDetails: FormattedIdentityProviderDetails): IdentityProviderFederatedAttributesErrors => {
    const federatedAttributesErrors: { [key: string]: React.ReactNode } = {};
    let count = 0;

    identityProviderDetails.companyIDPInfo.companyIDPAttributeMaps.forEach(attr => {
        if (!attr.idpField || !attr.attributeTypeID || !attr.fieldType) {
            count++;
            federatedAttributesErrors[attr.uuid] = getErrorAlert(resources.common.allRequiredFields);
        }
    });

    return {
        count,
        federatedAttributesErrors,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.identityProviderDetails.tabs.federatedAttributes.tabName} errorCount={count} />
    }
};